import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import { RenderTransactionStatus } from "components/common/RenderTransactionStatus";
import { GET_PLATFORM_REWARDS_REDEMPTIONS } from "graphql/platform";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { formatCashFull } from "utils/formatCash";
import { DEFAULT_PAGINATION_SIZE } from "components/common/Constants";
import TransactionsType from "enums/TransactionsType";
import type { PlatformTransactionRow } from "./types";

interface Row {
  id: string;
  date: string;
  company: string;
  destination: string;
  status: string;
  amount: string;
}

interface RedemptionsTableProps {
  platformAccountId: string;
}

const RedemptionsTable: React.FC<RedemptionsTableProps> = ({
  platformAccountId,
}) => {
  const { t } = useTranslation();

  const extractRows = (data: {
    [key: string]: { edges?: { node: PlatformTransactionRow }[] };
  }): Row[] => {
    const firstKey = Object.keys(data)[0];
    return (data[firstKey]?.edges ?? []).map(({ node }) => ({
      id: node.id,
      date: node.transactionDate
        ? new Date(node.transactionDate).toLocaleDateString()
        : "",
      company: node.companyName ?? "",
      destination:
        node.type === TransactionsType.REWARDS_TO_BANK
          ? t("Bank account")
          : t("Spending balance"),
      status: node.status,
      amount: formatCashFull(node.amount, node.currency),
    }));
  };

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_PLATFORM_REWARDS_REDEMPTIONS,
      initialVariables: {
        first: DEFAULT_PAGINATION_SIZE,
        after: undefined,
        platformAccountId,
      },
      extractData: extractRows,
    });

  const columns: DataColumn<Row>[] = useMemo(
    () => [
      { id: "date", header: t("Date"), cell: (row) => row.date },
      {
        id: "company",
        header: t("Account"),
        cell: (row) => row.company,
        flex: true,
      },
      {
        id: "destination",
        header: t("Paid to"),
        cell: (row) => row.destination,
      },
      {
        id: "status",
        header: t("Status"),
        cell: (row) => (
          <RenderTransactionStatus transactionStatus={row.status} smallPill />
        ),
      },
      {
        id: "amount",
        header: t("Amount"),
        align: "right",
        cell: (row) => row.amount,
      },
    ],
    [t],
  );

  return (
    <DataTable<Row>
      columns={columns}
      rows={dataArray}
      getRowKey={(row) => row.id}
      loading={displayLoading}
      isFetchingMore={isInfiniteScrollFetching}
      onScroll={handleScroll}
      emptyState={t("No redemptions yet")}
      loadingMoreText={t("Loading more redemptions")}
    />
  );
};

export default RedemptionsTable;
